import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiArrowLeft, FiArrowRight, FiCheck, FiArchive, FiMapPin, FiBox, FiDollarSign, FiInfo, FiPlus, FiCheckCircle, FiCopy, FiExternalLink, FiHash, FiCalendar, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import titleCase from '../utils/titleCase';
import { useAuth } from '../context/AuthContext';

// 3-step graphical wizard for creating a new asset.
// Replaces the old "everything in one modal" experience with:
//   1. Location  — pick the Functional Location (required, card grid)
//   2. Identity  — name, status (Active/Redundant card radios), cost centre
//   3. Details   — supplier, serial, capacity, dates, notes, then create
//
// Props:
//   flNodes:   FL tree from /api/functional-locations
//   costCodes: cost-centre list from /api/admin/cost-codes
//   existingNames: lowercase asset names from current tenant for duplicate check
//   onCancel:  () => void
//   onCreate:  (formData) => Promise<{ ok, error? }>  — caller does the POST
//
// State is local to the wizard; the parent only sees the final POST attempt.

const TOTAL_STEPS = 3;

export default function AssetCreateWizard({ flNodes = [], costCodes = [], existingNames = [], onCancel, onCreate }) {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const todayStr = new Date().toISOString().split('T')[0];
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // server response row when create succeeds
  const [copied, setCopied] = useState(false);
  const blankForm = {
    functional_location_id: '',
    name: '',
    status_id: '3',
    cost_code_id: '',
    supplier_name: '',
    serial_number: '',
    capacity: '',
    purchase_date: '',
    warranty_end: '',
    notes: '',
    parent_asset_id: '',
  };
  const [form, setForm] = useState(blankForm);
  const [flSearch, setFlSearch] = useState('');
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [parentLabel, setParentLabel] = useState('');

  // FL breadcrumb path (root → leaf) for any node.
  const pathFor = useMemo(() => {
    const byId = new Map(flNodes.map(n => [n.id, n]));
    return node => {
      const parts = [];
      let cur = node;
      for (let i = 0; i < 8 && cur; i++) {
        parts.unshift(cur);
        cur = cur.parent_id ? byId.get(cur.parent_id) : null;
      }
      return parts;
    };
  }, [flNodes]);

  const filteredFls = useMemo(() => {
    const q = flSearch.trim().toLowerCase();
    if (!q) return flNodes;
    return flNodes.filter(fl => {
      const path = pathFor(fl).map(p => `${p.code} ${p.name}`).join(' ').toLowerCase();
      return path.includes(q);
    });
  }, [flNodes, flSearch, pathFor]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedFl = flNodes.find(f => String(f.id) === String(form.functional_location_id));
  const nameDup = !!form.name && existingNames.includes(form.name.trim().toLowerCase());

  const canAdvance = (() => {
    if (step === 1) return !!form.name && !!form.cost_code_id && !nameDup;
    if (step === 2) return !!form.functional_location_id;
    return true;
  })();

  const handleNext = () => { setError(''); if (canAdvance) setStep(s => Math.min(TOTAL_STEPS, s + 1)); };
  const handleBack = () => { setError(''); setStep(s => Math.max(1, s - 1)); };
  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await onCreate({
        ...form,
        name: form.name.trim(),
        supplier_name: (form.supplier_name || '').trim() || null,
      });
      if (!res?.ok) { setError(res?.error || 'Failed to create asset'); return; }
      setCreated(res.asset || null);
      setStep(4);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAnother = () => {
    setCreated(null);
    setCopied(false);
    setForm(blankForm);
    setFlSearch('');
    setParentSearch('');
    setParentLabel('');
    setError('');
    setStep(1);
  };

  const handleCopyId = () => {
    if (!created?.tag) return;
    navigator.clipboard?.writeText(created.tag).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  // ──────────── styling primitives ────────────
  const card = { background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        style={{ ...card, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-xl)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
              {step === 4 ? 'Asset created' : 'Create Asset'}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {step === 4 ? 'Saved successfully' : `Step ${step} of ${TOTAL_STEPS}`}
            </div>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={20} /></button>
        </div>

        {/* Stepper */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6 }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: step === 4 ? '#16a34a' : (n <= step ? '#2563eb' : 'var(--bg-input)'),
              transition: 'background 0.25s',
            }} />
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <AnimatePresence mode="wait">
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiMapPin size={16} color="#2563eb" /> Where does this asset live?
                </h4>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Pick the Functional Location it belongs to. You can move it later.</div>
                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input autoFocus value={flSearch} onChange={e => setFlSearch(e.target.value)} placeholder="Search by code or name…"
                    style={{ ...inputStyle, paddingLeft: 36 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                  {filteredFls.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>
                      No Functional Locations match. {flNodes.length === 0 && 'Create one in the Functional Locations tab first.'}
                    </div>
                  )}
                  {filteredFls.map((fl, i) => {
                    const path = pathFor(fl);
                    const isSelected = String(form.functional_location_id) === String(fl.id);
                    return (
                      <button key={fl.id} onClick={() => set('functional_location_id', String(fl.id))}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '9px 12px', cursor: 'pointer', width: '100%',
                          border: 'none', borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                          background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                        }}>
                        {isSelected
                          ? <div style={{ flexShrink: 0, width: 16, height: 16, borderRadius: 8, background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiCheck size={10} /></div>
                          : <div style={{ flexShrink: 0, width: 16 }} />}
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', flexShrink: 0 }}>{fl.code}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{fl.name}</span>
                        {path.length > 1 && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140, flexShrink: 0 }}>
                            {path.slice(0, -1).map(p => p.code).join(' / ')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiBox size={16} color="#2563eb" /> Identity
                </h4>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>The 9-digit Asset ID is assigned automatically when you save.</div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Name <span style={{ color: '#dc2626' }}>*</span></label>
                  <input autoFocus value={form.name} onChange={e => set('name', titleCase(e.target.value))} placeholder="e.g. Caravan Bay 3" style={inputStyle} />
                  {nameDup && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>An asset with that name already exists.</div>}
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Status</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      { id: '3', name: 'Active', icon: FiCheck, color: '#3C802C', desc: 'Live — appears in pickers and gets scheduled work' },
                      { id: '4', name: 'Redundant', icon: FiArchive, color: '#7A7982', desc: 'Retired — hidden by default, no new work orders' },
                    ].map(s => {
                      const Selected = form.status_id === s.id;
                      const Icon = s.icon;
                      return (
                        <button key={s.id} onClick={() => set('status_id', s.id)}
                          style={{
                            textAlign: 'left', padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                            border: `2px solid ${Selected ? s.color : 'var(--border)'}`,
                            background: Selected ? `${s.color}10` : 'var(--bg-input)',
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 800, color: s.color, marginBottom: 4 }}>
                            <Icon size={14} /> {s.name}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Cost Centre <span style={{ color: '#dc2626' }}>*</span></label>
                  <select value={form.cost_code_id} onChange={e => set('cost_code_id', e.target.value)} style={inputStyle}>
                    <option value="">Select cost centre…</option>
                    {costCodes.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name || cc.description}</option>)}
                  </select>
                </div>
              </motion.div>
            )}

            {step === 4 && created && (
              <motion.div key="step4" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}>
                {/* Big celebratory header */}
                <div style={{ textAlign: 'center', padding: '8px 0 18px' }}>
                  <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 180, damping: 14 }}
                    style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #16a34a, #22c55e)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: '0 12px 28px rgba(22,163,74,0.35)' }}>
                    <FiCheckCircle size={36} color="#fff" />
                  </motion.div>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>Asset created</h2>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Your new asset is ready.</div>
                </div>

                {/* Hero card: Asset ID + name + status */}
                <div style={{ padding: 20, borderRadius: 14, background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(124,58,237,0.05))', border: '1px solid rgba(37,99,235,0.25)', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', color: '#2563eb', marginBottom: 6 }}>ASSET ID</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '1.5px' }}>{created.tag || '—'}</span>
                        <button onClick={handleCopyId} title="Copy Asset ID"
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: copied ? '#16a34a' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {copied ? <><FiCheck size={11} /> Copied</> : <><FiCopy size={11} /> Copy</>}
                        </button>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 10 }}>{created.name}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: `${created.status_color || '#3C802C'}15`, color: created.status_color || '#3C802C' }}>{created.status_name || 'Active'}</span>
                    </div>
                  </div>
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 6 }}>
                  {created.functional_location_code && (
                    <SummaryItem icon={FiMapPin} label="Functional Location" value={`${created.functional_location_code} — ${created.functional_location_name || ''}`} />
                  )}
                  {(created.cost_code_code || created.cost_code_name) && (
                    <SummaryItem icon={FiDollarSign} label="Cost Centre" value={[created.cost_code_code, created.cost_code_name].filter(Boolean).join(' — ')} />
                  )}
                  {created.supplier_name && <SummaryItem icon={FiBox} label="Supplier" value={created.supplier_name} />}
                  {created.serial_number && <SummaryItem icon={FiHash} label="Serial Number" value={created.serial_number} />}
                  {created.capacity && <SummaryItem icon={FiBox} label="Capacity" value={created.capacity} />}
                  {created.purchase_date && <SummaryItem icon={FiCalendar} label="Purchase Date" value={created.purchase_date} />}
                  {created.warranty_end && <SummaryItem icon={FiCalendar} label="Warranty End" value={created.warranty_end} />}
                </div>
                {created.notes && (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)', display: 'flex', gap: 10 }}>
                    <FiFileText size={14} style={{ marginTop: 2, color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: '0.5px' }}>NOTES</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{created.notes}</div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 18, padding: 12, borderRadius: 10, background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.18)' }}>
                  <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiInfo size={13} /> Next: print &amp; attach a QR label so technicians can scan to find this asset. The QR option is on the asset detail page.
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiInfo size={16} color="#2563eb" /> Optional details
                </h4>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>All optional. Fill what you have — you can edit any of this later.</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={labelStyle}>Supplier</label>
                    <input value={form.supplier_name} onChange={e => set('supplier_name', titleCase(e.target.value))} placeholder="e.g. Acme Industrial" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Serial Number</label>
                    <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="Optional" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Capacity</label>
                    <input value={form.capacity} onChange={e => set('capacity', e.target.value)} placeholder="e.g. 500L, 12 berth" style={inputStyle} />
                  </div>
                  <div />
                  <div>
                    <label style={labelStyle}>Purchase Date</label>
                    <input type="date" value={form.purchase_date} max={todayStr} onChange={e => set('purchase_date', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Warranty End</label>
                    <input type="date" value={form.warranty_end} min={todayStr} onChange={e => set('warranty_end', e.target.value)} style={inputStyle} />
                  </div>
                </div>

                <div style={{ marginBottom: 18, position: 'relative' }}>
                  <label style={labelStyle}>Parent Asset (optional)</label>
                  {form.parent_asset_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(139,92,246,0.08)' }}>
                      <FiBox size={13} style={{ color: '#8b5cf6' }} />
                      <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{parentLabel || `Asset #${form.parent_asset_id}`}</span>
                      <button type="button" onClick={() => { set('parent_asset_id', ''); setParentLabel(''); setParentSearch(''); }}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Clear</button>
                    </div>
                  ) : (
                    <input value={parentSearch} onFocus={() => setParentPickerOpen(true)}
                      onChange={e => { const q = e.target.value; setParentSearch(q); setParentPickerOpen(true);
                        authFetch(`/api/assets?search=${encodeURIComponent(q)}&limit=30`).then(r => r.ok ? r.json() : { data: [] })
                          .then(d => setParentResults(d.data || []))
                          .catch(() => setParentResults([]));
                      }}
                      placeholder="Search by Asset ID or name — for sub-assets…"
                      style={inputStyle} />
                  )}
                  {parentPickerOpen && !form.parent_asset_id && parentResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 240, overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                      {parentResults.slice(0, 30).map(a => (
                        <div key={a.id}
                          onMouseDown={e => { e.preventDefault(); set('parent_asset_id', a.id); setParentLabel(`${a.tag} — ${a.name}`); setParentPickerOpen(false); setParentSearch(''); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#8b5cf6' }}>{a.tag}</span>
                          <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>{a.name}</span>
                          {a.functional_location_code && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-muted)' }}>({a.functional_location_code})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {form.parent_asset_id && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      This asset will appear as a sub-asset of the one selected above.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} />
                </div>

                {/* Review summary */}
                <div style={{ padding: 14, borderRadius: 10, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.2)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', marginBottom: 8, letterSpacing: '0.4px' }}>READY TO CREATE</div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{form.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {selectedFl ? `${selectedFl.code} — ${selectedFl.name}` : ''}
                    {' · '}
                    {form.status_id === '4' ? 'Redundant' : 'Active'}
                    {form.supplier_name ? ` · ${form.supplier_name}` : ''}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{step !== 4 ? error : ''}</div>
          {step === 4 ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {created?.id && (
                <button onClick={() => { onCancel(); navigate(`/assets/${created.id}`); }}
                  style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiExternalLink size={13} /> View asset
                </button>
              )}
              <button onClick={handleCreateAnother}
                style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #2563eb', background: 'rgba(37,99,235,0.08)', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiPlus size={13} /> Create another
              </button>
              <button onClick={onCancel}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Done
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              {step > 1 && (
                <button onClick={handleBack} disabled={saving}
                  style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiArrowLeft size={13} /> Back
                </button>
              )}
              {step < TOTAL_STEPS ? (
                <button onClick={handleNext} disabled={!canAdvance}
                  style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: canAdvance ? '#2563eb' : '#94a3b8', color: '#fff', cursor: canAdvance ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Next <FiArrowRight size={13} />
                </button>
              ) : (
                <button onClick={handleCreate} disabled={saving || !form.name || !form.cost_code_id || !form.functional_location_id}
                  style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#16a34a', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {saving ? 'Creating…' : <><FiPlus size={13} /> Create Asset</>}
                </button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function SummaryItem({ icon: Icon, label, value }) {
  return (
    <div style={{ padding: 12, borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.5px', marginBottom: 4 }}>
        <Icon size={12} /> {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}
